import type { GridGeometry } from '../grid';
import type { MapDefinition, TilePosition } from '../model/gamestate/GameState';

interface ReachableDestination {
  position: TilePosition;
  distance: number;
  pathValue: number;
  distanceToTarget: number;
}

function makeKey(position: TilePosition): string {
  return `${position.x},${position.y}`;
}

function isWithinBounds(map: MapDefinition, position: TilePosition): boolean {
  return position.x >= 0 && position.y >= 0 && position.x < map.width && position.y < map.height;
}

function isWalkable(map: MapDefinition, position: TilePosition): boolean {
  return Boolean(map.tiles[position.y]?.[position.x]?.walkable);
}

function getPathValue(map: MapDefinition, position: TilePosition): number {
  const tile = map.tiles[position.y]?.[position.x];
  if (!tile) {
    return 0;
  }
  const pathLayerValue =
    typeof tile.pathValue === 'number' && Number.isFinite(tile.pathValue)
      ? Math.max(0, Math.floor(tile.pathValue))
      : 0;
  if (pathLayerValue > 0) {
    return pathLayerValue;
  }
  return tile.terrain === 'path' ? 1 : 0;
}

function isPathTile(map: MapDefinition, position: TilePosition): boolean {
  return getPathValue(map, position) > 0;
}

export function getEffectiveMoveDistance(
  map: MapDefinition,
  position: TilePosition,
  baseMoveDistance: number,
): number {
  const normalizedBase = Math.max(1, Math.floor(baseMoveDistance));
  return isPathTile(map, position) ? normalizedBase + 1 : normalizedBase;
}

function reachableDestinations(
  map: MapDefinition,
  grid: GridGeometry,
  start: TilePosition,
  moveDistance: number,
  target: TilePosition,
): ReachableDestination[] {
  const effectiveMoveDistance = getEffectiveMoveDistance(map, start, moveDistance);
  const queue: Array<{ position: TilePosition; distance: number }> = [{ position: start, distance: 0 }];
  const visited = new Set<string>([makeKey(start)]);
  const reachable: ReachableDestination[] = [];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current.distance >= effectiveMoveDistance) {
      continue;
    }

    for (const neighbor of grid.neighbors(current.position.x, current.position.y)) {
      const key = makeKey(neighbor);
      if (visited.has(key) || !isWithinBounds(map, neighbor) || !isWalkable(map, neighbor)) {
        continue;
      }

      visited.add(key);
      const nextDistance = current.distance + 1;
      queue.push({ position: neighbor, distance: nextDistance });
      reachable.push({
        position: neighbor,
        distance: nextDistance,
        pathValue: getPathValue(map, neighbor),
        distanceToTarget: grid.distance(neighbor.x, neighbor.y, target.x, target.y),
      });
    }
  }

  reachable.sort((a, b) => (
    a.distanceToTarget - b.distanceToTarget
    || b.pathValue - a.pathValue
    || b.distance - a.distance
    || a.position.y - b.position.y
    || a.position.x - b.position.x
  ));
  return reachable;
}

export function findMovePath(
  map: MapDefinition,
  grid: GridGeometry,
  start: TilePosition,
  target: TilePosition,
  moveDistance: number,
  _startFacing?: number,
): TilePosition[] | null {
  if (!isWithinBounds(map, target) || !isWalkable(map, target)) {
    return null;
  }
  if (start.x === target.x && start.y === target.y) {
    return [];
  }

  const startKey = makeKey(start);
  const targetKey = makeKey(target);
  const frontier: TilePosition[] = [start];
  const cameFrom = new Map<string, string | null>([[startKey, null]]);
  const positions = new Map<string, TilePosition>([[startKey, start]]);

  while (frontier.length > 0) {
    const current = frontier.shift()!;
    const currentKey = makeKey(current);
    if (currentKey === targetKey) {
      break;
    }

    for (const next of reachableDestinations(map, grid, current, moveDistance, target)) {
      const nextKey = makeKey(next.position);
      if (cameFrom.has(nextKey)) {
        continue;
      }
      cameFrom.set(nextKey, currentKey);
      positions.set(nextKey, next.position);
      if (nextKey === targetKey) {
        frontier.length = 0;
        break;
      }
      frontier.push(next.position);
    }
  }

  if (!cameFrom.has(targetKey)) {
    return null;
  }

  const path: TilePosition[] = [];
  let cursor: string | null = targetKey;
  while (cursor && cursor !== startKey) {
    const position = positions.get(cursor);
    if (!position) {
      return null;
    }
    path.push(position);
    cursor = cameFrom.get(cursor) ?? null;
  }
  path.reverse();
  return path;
}
